import { useState, useEffect, useContext, useCallback } from "react";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { HiOutlineGift } from "react-icons/hi2";
import CryptoJS from "crypto-js";
import { ClipLoader } from "react-spinners";

import axiosInstance from "../../Context/axiosInstance";
import { DarkModeContext } from "../../Context/DarkModeContext";
import { GeoContext } from "../../Context/GeoContext.js";
import AddCourseModal from "../../Components/BuyCourseModal";
import CatalogueCartPanel from "../../Components/CatalogueCartPanel";
import { visibleItems } from "../../utils/visibility";
import { getDiscountPercentage, getFinalPrice } from "../../utils/pricing";
import PriceDisplay from "../../Components/PriceDisplay";
// import StudentWalletActions from "../../Components/StudentWalletActions";

const CataloguePage = () => {
  const { isDarkMode } = useContext(DarkModeContext);
  const { store, loadingGeo } = useContext(GeoContext);
  const storeId = store?.id;
  const currency = store?.currency;

  // STATE HOOKS
  const [cartItems, setCartItems] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [selectedTab, setSelectedTab] = useState("videos");

  const userId = localStorage.getItem("userId");
  const [balance, setBalance] = useState(null);
  const [loadingBalance, setLoadingBalance] = useState(true);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [formData, setFormData] = useState({
    courseImageUrl: "",
    courseName: "",
  });
  const [loadingaddCourse, setLoadingaddCourse] = useState(false);

  // Pagination states
  const [sortColumn, setSortColumn] = useState("Title");
  const [sortDirection, setSortDirection] = useState("asc");
  const [searchQuery, setSearchQuery] = useState("");
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const tabs = ["videos", "materials"];

  const refreshBalance = useCallback(async () => {
    setLoadingBalance(true);
    try {
      const id =
        userId &&
        CryptoJS.AES.decrypt(userId, process.env.REACT_APP_SECRET_KEY).toString(
          CryptoJS.enc.Utf8,
        );
      if (!id) throw new Error("Missing wallet user");
      const { data } = await axiosInstance.get("/users/" + id + "/wallet");
      const value = data.balance == null ? NaN : Number(data.balance);
      if (!Number.isFinite(value)) throw new Error("Invalid wallet balance");
      setBalance(value);
      return value;
    } catch {
      setBalance(null);
      return null;
    } finally {
      setLoadingBalance(false);
    }
  }, [userId]);

  useEffect(() => {
    refreshBalance();
    window.addEventListener("focus", refreshBalance);
    return () => window.removeEventListener("focus", refreshBalance);
  }, [refreshBalance, storeId]);

  // Function to fetch data based on selected tab
  const fetchItems = useCallback(async () => {
    if (!store || loadingGeo) return;
    setIsLoading(true);
    try {
      const endpoint = `/catalogs/${selectedTab}?page=${page}&pageSize=${pageSize}&sortColumn=${sortColumn}&sortDirection=${sortDirection}&search=${encodeURIComponent(
        searchQuery,
      )}&storeId=${storeId}`;
      const response = await axiosInstance.get(endpoint);

      if (response.data && response.data.response) {
        // Append the new items only when the page is valid
        setItems(visibleItems(response.data.response));

        const calculatedTotalPages = Math.ceil(
          response.data.totalCount / pageSize,
        );
        setTotalPages(calculatedTotalPages);

        // Adjust page if it exceeds total pages
        if (page > calculatedTotalPages) {
          setPage(calculatedTotalPages);
        }
      }
    } catch (error) {
    } finally {
      setIsLoading(false);
    }
  }, [
    selectedTab,
    page,
    pageSize,
    searchQuery,
    sortColumn,
    sortDirection,
    store,
    storeId,
    loadingGeo,
  ]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  useEffect(() => {
    // Reset to page 1 whenever the search query changes
    setPage(1);
  }, [searchQuery]);

  useEffect(() => {
    try {
      const storedCartItems =
        JSON.parse(localStorage.getItem("cartItems")) || [];
      if (Array.isArray(storedCartItems)) {
        setCartItems(storedCartItems);
      } else {
        localStorage.removeItem("cartItems"); // Clear invalid data
      }
    } catch (error) {
      localStorage.removeItem("cartItems"); // Clear invalid data
    }
  }, []);

  const categoryTypes = {
    materials: "material",
    videos: "video",
  };

  const addToCart = (item) => {
    const type = categoryTypes[selectedTab]; // Get the type from the selected tab

    if (type) {
      const itemWithType = {
        ...item,
        type: type, // Add the type to the item
      };

      // Check if the item already exists in the cart based on id and type
      const itemExists = cartItems.some(
        (cartItem) =>
          cartItem.id === itemWithType.id &&
          cartItem.type === itemWithType.type,
      );

      if (itemExists) {
        toast.info("This item is already in the cart.", {
          position: "top-center",
          autoClose: 3000,
          hideProgressBar: false,
          pauseOnHover: false,
        });
        return; // Stop the function if the item already exists
      }

      // Add the item to the cart if it doesn't exist
      setCartItems((prev) => {
        const updatedCart = [...prev, itemWithType];
        localStorage.setItem("cartItems", JSON.stringify(updatedCart));
        return updatedCart;
      });

      const truncatedName =
        item.title.length > 20 ? item.title.slice(0, 20) + "..." : item.title;
      toast.success(`${truncatedName} added to cart`, {
        position: "top-center",
        autoClose: 3000,
        hideProgressBar: false,
        pauseOnHover: false,
      });
    } else {
      toast.error("Invalid item type. Please try again.", {
        position: "top-center",
        autoClose: 3000,
        hideProgressBar: false,
        pauseOnHover: false,
      });
    }
  };

  const deleteFromCart = (id, type) => {
    setCartItems((prevCartItems) => {
      const updatedCart = prevCartItems.filter(
        (item) => !(item.id === id && item.type === type),
      );
      localStorage.setItem("cartItems", JSON.stringify(updatedCart));
      return updatedCart;
    });
  };

  const handleCheckout = async () => {
    const totalCartPrice = cartItems.reduce(
      (acc, item) => acc + getFinalPrice(item),
      0,
    );
    const currentBalance = await refreshBalance();
    if (currentBalance === null) {
      toast.error("Could not check your wallet. Please try again.");
      return;
    }
    if (currentBalance >= totalCartPrice) {
      openAddCourseModal(true);
    } else {
      toast.error("Insufficient balance", {
        position: "top-center",
        autoClose: 3000,
        hideProgressBar: false,
        pauseOnHover: false,
      });
    }
  };

  const openAddCourseModal = () => {
    setAddModalOpen(true); // Open the modal
  };

  const handleAdd = async () => {
    const courseName = formData.courseName?.trim() || "";

    if (!courseName) {
      setFormErrors({
        courseName: "Course name is required.",
      });
      return;
    }

    if (courseName.includes("/")) {
      setFormErrors({
        courseName: 'Course name cannot contain "/".',
      });
      return;
    }

    setFormErrors({});

    if (loadingaddCourse) return;

    // Initialize empty arrays for materials, and videos
    let materialsIds = [];
    let videosIds = [];

    // Calculate the total price of items in the cart
    let totalAmount = 0;
    cartItems.forEach((item) => {
      if (item.id > 0) {
        totalAmount += getFinalPrice(item);
        // Only include items that have a positive ID
        if (item.type === "video") {
          videosIds.push(item.id);
        } else if (item.type === "material") {
          materialsIds.push(item.id);
        }
      }
    });

    // Check if there are cart items
    if (cartItems.length === 0) {
      toast.error("Your cart is empty. Please add items to your cart.", {
        position: "top-center",
        autoClose: 3000,
        hideProgressBar: false,
        pauseOnHover: false,
      });
      return;
    }

    // Check if the user has enough balance to make the purchase
    setLoadingaddCourse(true);
    const currentBalance = await refreshBalance();
    if (currentBalance === null) {
      setLoadingaddCourse(false);
      toast.error("Could not check your wallet. Please try again.");
      return;
    }
    if (currentBalance < totalAmount) {
      setLoadingaddCourse(false);
      toast.error("You don't have enough balance to purchase this course.", {
        position: "top-center",
        autoClose: 3000,
      });
      return;
    }

    // Construct the payload for the POST request
    const payload = {
      name: courseName,
      imageUrl:
        "https://img.freepik.com/free-vector/blue-curve-background_53876-113112.jpg?semt=ais_hybrid",
      storeId,
    };

    // Only add arrays if they are not empty (i.e., contain valid IDs)
    if (materialsIds.length > 0) payload.materialsIds = materialsIds;
    if (videosIds.length > 0) payload.videosIds = videosIds;

    try {
      const response = await axiosInstance.post(
        "/courses/purchase/custom",
        payload,
        {
          headers: {
            "Content-Type": "application/json",
          },
        },
      );

      if (response.status === 200) {
        await refreshBalance();
        toast.success("Course Created Successfully!", {
          position: "top-center",
          autoClose: 3000,
        });
        setCartItems([]);
        localStorage.removeItem("cartItems");
        handleCloseAddModal();
      } else {
        toast.error("Failed to add course, no data returned from the server.", {
          position: "top-center",
          autoClose: 3000,
        });
      }
    } catch (error) {
      toast.error("Something went wrong. Please try again.", {
        position: "top-center",
        autoClose: 3000,
      });
    } finally {
      setLoadingaddCourse(false);
    }
  };

  const handleCloseAddModal = () => {
    setAddModalOpen(false);
    setFormErrors({});
    setFormData({
      courseName: "",
      courseImageUrl: "",
    });
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
    }
  };

  // control sorting direction
  const toggleSortDirection = (column) => {
    if (sortColumn === column) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  // Handle tab selection
  const handleTabSelect = (tab) => {
    setSelectedTab(tab);
    setSearchQuery("");
    setPage(1);
  };

  return (
    <div
      className={`flex flex-col lg:flex-row lg:items-start ${
        isDarkMode ? "bg-[#181818]" : "bg-customBlue"
      } min-h-screen min-w-0 flex-1 px-4 pt-6 lg:px-8`}
    >
      <div
        className={`${
          isDarkMode ? "bg-[#1e1e1e]" : "bg-white"
        } min-w-0 flex-1 rounded-lg p-4 shadow-xl lg:p-8`}
      >
        <div className="flex min-w-0 flex-1 flex-col">
          <div className="mb-3 flex flex-col items-center justify-between gap-3 sm:flex-row sm:items-start">
            <h2 className="text-center text-2xl sm:text-3xl font-semibold sm:text-left">
              Customize Your Learnings
            </h2>
            {/* <StudentWalletActions
              balance={balance}
              currency={currency}
              loading={loadingBalance}
              isDarkMode={isDarkMode}
              showFillWallet={storeId === 1}
            /> */}
          </div>
          {/* Custom Tab Bar */}
          <div
            className={`flex justify-center ${
              isDarkMode ? "text-white" : "text-black"
            }`}
          >
            <div className="relative grid w-full max-w-[420px] grid-cols-4">
              {tabs.map((tab) => (
                <button
                  key={tab}
                  type="button"
                  onClick={() => handleTabSelect(tab)}
                  className={`min-w-0 px-1 py-2 text-center text-sm capitalize transition-colors sm:px-2 sm:text-base ${
                    selectedTab === tab ? "text-blue-400" : "text-gray-600"
                  }`}
                >
                  {tab}
                </button>
              ))}

              <div
                className="pointer-events-none absolute bottom-0 left-0 h-1 w-1/4 bg-blue-400 transition-transform duration-300 ease-in-out"
                style={{
                  transform: `translateX(${tabs.indexOf(selectedTab) * 100}%)`,
                }}
              />
            </div>
          </div>

          <div className="flex w-full justify-center lg:justify-start">
            {/* Search Section on the Left */}
            <div className="flex justify-start items-center mt-6 w-full max-w-[500px] lg:max-w-[300px] max-w-full">
              <input
                type="text"
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className={`${
                  isDarkMode ? "bg-[#292929]" : "bg-gray-100"
                } px-4 py-2 text-center lg:text-left border border-gray-300 rounded-lg w-full`}
              />
            </div>
          </div>

          <div className="mt-3 grid items-start gap-4 xl:min-h-0 xl:flex-1 min-[1440px]:grid-cols-[minmax(0,1fr)_340px] xl:grid-rows-[minmax(0,1fr)] xl:items-stretch xl:overflow-hidden">
            <div className="min-w-0 xl:flex xl:min-h-0 xl:flex-col">
              <div
                className={`h-[clamp(320px,65dvh,550px)] overflow-auto rounded-2xl border shadow-sm
                  [scrollbar-width:thin]
                  [scrollbar-color:#94a3b8_transparent]
                  [&::-webkit-scrollbar]:h-[9px]
                  [&::-webkit-scrollbar]:w-[9px]
                  [&::-webkit-scrollbar-track]:bg-transparent
                  [&::-webkit-scrollbar-track]:[margin-block:0.65rem]
                  [&::-webkit-scrollbar-thumb]:rounded-full
                  [&::-webkit-scrollbar-thumb]:border-2
                  [&::-webkit-scrollbar-thumb]:border-transparent
                  [&::-webkit-scrollbar-thumb]:bg-slate-400
                  [&::-webkit-scrollbar-thumb]:[background-clip:padding-box]
                  [&::-webkit-scrollbar-thumb:hover]:bg-slate-500
                  [&::-webkit-scrollbar-corner]:bg-transparent
                   min-w-0 max-w-full ${
                     isDarkMode
                       ? "border-gray-700 bg-transparent"
                       : "border-gray-200 bg-transparent"
                   }   overflow-x-auto`}
              >
                <table
                  className={`w-full min-w-[760px] table-fixed border-collapse ${
                    isDarkMode ? "text-white" : "text-gray-800"
                  }`}
                >
                  <colgroup>
                    <col className="w-[24%]" />
                    <col className="w-[24%]" />
                    <col className="w-[24%]" />
                    <col className="w-[24%]" />
                    <col className="w-[24%]" />
                  </colgroup>
                  <thead className="sticky top-0">
                    <tr className="border-b border-gray-200 text-[11px] uppercase tracking-[0.12em] text-gray-500">
                      <th
                        onClick={() => toggleSortDirection("Title")}
                        className="cursor-pointer px-4 py-3.5 text-left font-semibold transition hover:text-blue-500"
                      >
                        Title
                        {sortColumn === "Title" && (
                          <span className="text-sm px-1 items-center text-blue-400">
                            {sortDirection === "asc" ? "▲" : "▼"}
                          </span>
                        )}
                      </th>
                      <th className="px-4 py-3.5 text-left font-semibold">
                        Description
                      </th>
                      <th
                        onClick={() => toggleSortDirection("Price")}
                        className="cursor-pointer px-4 py-3.5 text-left font-semibold transition hover:text-blue-500"
                      >
                        Price
                        {sortColumn === "Price" && (
                          <span className="text-sm px-1 items-center text-blue-400 ease-in-out">
                            {sortDirection === "asc" ? "▲" : "▼"}
                          </span>
                        )}
                      </th>
                      <th className="px-4 py-3.5 text-center font-semibold">
                        Discount
                      </th>
                      <th className="px-4 py-3.5 text-center font-semibold">
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody
                    className={
                      isDarkMode
                        ? "divide-y divide-gray-700"
                        : "divide-y divide-gray-100"
                    }
                  >
                    {isLoading ? (
                      <tr>
                        <td colSpan="5" className="relative text-center py-4">
                          <div className="absolute top-[350px] inset-0 flex justify-center items-center bg-opacity-50 z-10">
                            <ClipLoader color="#2264E5" size={50} />
                          </div>
                        </td>
                      </tr>
                    ) : items.length === 0 ? (
                      <tr>
                        <td
                          colSpan="5"
                          className="text-center py-4 text-gray-600"
                        >
                          No {selectedTab} available yet
                        </td>
                      </tr>
                    ) : (
                      items.map((item) => {
                        const itemType = categoryTypes[selectedTab];
                        const isAdded = cartItems.some(
                          (cartItem) =>
                            cartItem.id === item.id &&
                            cartItem.type === itemType,
                        );
                        return (
                          <tr
                            key={`${item.id}-${item.type}`}
                            className={`group relative text-left transition-colors duration-150 ${
                              isAdded
                                ? isDarkMode
                                  ? "bg-emerald-700/60 hover:bg-emerald-700/70"
                                  : "bg-emerald-50"
                                : isDarkMode
                                  ? "bg-[#1e1e1e] hover:bg-[#292929]"
                                  : "bg-white hover:bg-slate-50"
                            }`}
                          >
                            <td className="px-4 py-3.5">
                              <p
                                className="truncate font-semibold"
                                title={item.title}
                              >
                                {item.title}
                              </p>
                              <span className="mt-1 inline-block rounded-full bg-blue-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-blue-600">
                                {categoryTypes[selectedTab]}
                              </span>
                            </td>

                            <td className="px-4 py-3.5">
                              <p
                                className="line-clamp-2 text-sm leading-5 text-gray-500"
                                title={item.description}
                              >
                                {item.description || "No description provided"}
                              </p>
                            </td>

                            <td className="whitespace-nowrap px-4 py-3.5">
                              {item.price && item.price > 0 ? (
                                <PriceDisplay
                                  item={item}
                                  currency={currency}
                                  showBadge={false}
                                />
                              ) : (
                                <span className="inline-flex items-center overflow-hidden rounded-lg border border-amber-200 bg-amber-50 text-xs font-bold shadow-sm">
                                  <span className="flex self-stretch items-center bg-amber-400 px-2 text-amber-950">
                                    <HiOutlineGift className="text-sm" />
                                  </span>

                                  <span className="px-2.5 py-1 text-amber-700">
                                    FREE
                                  </span>
                                </span>
                              )}
                            </td>

                            <td className="px-4 py-3.5 text-center">
                              {getDiscountPercentage(item) > 0 ? (
                                <span className="inline-flex rounded-full bg-red-100 px-2.5 py-1 text-xs font-bold text-red-600">
                                  {getDiscountPercentage(item)}% OFF
                                </span>
                              ) : (
                                <span className="text-gray-400">—</span>
                              )}
                            </td>

                            <td className="px-4 py-5 text-right flex justify-center">
                              <button
                                onClick={() => addToCart(item)}
                                disabled={isAdded}
                                className={`whitespace-nowrap rounded-lg px-2 py-2 text-xs font-semibold transition-all xl:min-w-[108px] ${
                                  isAdded
                                    ? "cursor-default border border-emerald-200 bg-emerald-100 text-emerald-700"
                                    : "bg-customBlue text-white shadow-sm hover:-translate-y-0.5 hover:bg-blue-800 hover:shadow-md"
                                }`}
                              >
                                {isAdded ? "Added ✓" : "Add to Cart"}
                              </button>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>

              {/* Dropdown to choose items per page */}
              <div className="flex items-center justify-end py-4 text-black">
                <label
                  htmlFor="pageSize"
                  className={`${
                    isDarkMode ? "text-white" : "text-gray-700"
                  } mr-2 text-sm`}
                >
                  Items per page:
                </label>
                <select
                  id="pageSize"
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value));
                    setPage(1);
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-lg"
                >
                  {[10, 20, 30, 50].map((size) => (
                    <option key={size} value={size}>
                      {size}
                    </option>
                  ))}
                </select>
              </div>

              {/* Pagination Controls */}
              <div className="mt-4 flex flex-col sm:flex-row justify-between items-center">
                <button
                  onClick={() => handlePageChange(page - 1)}
                  disabled={page <= 1}
                  className={`px-4 py-2 bg-gray-300 text-black rounded-lg ${
                    page <= 1
                      ? "opacity-50 cursor-not-allowed"
                      : "hover:bg-gray-400"
                  }`}
                >
                  Previous
                </button>
                <span className={"text-lg mt-2 sm:mt-0"}>
                  Page {page} of {totalPages}
                </span>
                <button
                  onClick={() => handlePageChange(page + 1)}
                  disabled={page >= totalPages}
                  className={`px-4 py-2 bg-gray-300 text-black rounded-lg ${
                    page >= totalPages
                      ? "opacity-50 cursor-not-allowed"
                      : "hover:bg-gray-400"
                  }`}
                >
                  Next
                </button>
              </div>
            </div>

            <CatalogueCartPanel
              items={cartItems}
              currency={currency}
              balance={balance}
              loadingBalance={loadingBalance}
              onRemove={deleteFromCart}
              onCheckout={handleCheckout}
              isDarkMode={isDarkMode}
              loading={loadingaddCourse}
            />
          </div>
        </div>

        <AddCourseModal
          isOpen={addModalOpen}
          onClose={handleCloseAddModal}
          courseName={formData.courseName}
          onChange={(value) => {
            setFormData((s) => ({ ...s, courseName: value }));
            if (formErrors.courseName) {
              setFormErrors((current) => ({
                ...current,
                courseName: "",
              }));
            }
          }}
          onConfirm={handleAdd}
          loading={loadingaddCourse}
          errors={formErrors}
        />
      </div>
    </div>
  );
};

export default CataloguePage;
